import { getSerialPortScanner, getSerialPortScale, SerialPortScale } from "@q-food/q-food";

const websocket: Ref<WebSocket | undefined> = ref();
let reconnectInterval: number | null = null;
let pingInterval: number | null = null;
let scaleStatusCheck = null;
let scannerStatusCheck = null;
export const hasBridgeIntegration = ref(false);
let tries = 0;
export const downloadUrl = ref<string | null>(null);
export const currentVersion = ref<string | null>(null);
export const latestVersion = ref<string | null>(null);

let scaleHandler: (() => void) | null = null;
let scannerHandler: (() => void) | null = null;
const lastMessageTime = ref(0);
const lastScannerMessage = ref(0);

const WS_BASE_URL = import.meta.env.VITE_PULSEFOOD_BRIDGE_HOST ?? "ws://localhost:1337/bridge";

export function getWebSocket(): Ref<WebSocket | undefined> {
	return websocket;
}

type ScaleState = {
	isConnected: Ref<boolean>;
	lastMessageTime: Ref<number>;
	currentWeight: Ref<number>;
};

type ScannerState = {
	isConnected: Ref<boolean>;
	lastScannerMessage: Ref<number>;
	valueToUpdate: Ref<string>;
};

export function startWebSocket(): void {
	if (reconnectInterval != null) return; // already running

	attemptConnection();

	// @ts-ignore
	reconnectInterval = setInterval(() => {
		const state = websocket.value?.readyState;

		if (state === undefined || state === WebSocket.CLOSED || state === WebSocket.CLOSING) {
			if (!hasBridgeIntegration.value) tries++;
			attemptConnection();
		} else if (state === WebSocket.OPEN) {
			hasBridgeIntegration.value = true;
			tries = 0;
		}
		// CONNECTING: a connect is already in progress — leave it alone.

		if (tries >= 5 && !hasBridgeIntegration.value && reconnectInterval != null) {
			clearInterval(reconnectInterval);
			reconnectInterval = null;
		}
	}, 2000);
}

export function stopWebSocket(): void {
	if (reconnectInterval != null) {
		clearInterval(reconnectInterval);
		reconnectInterval = null;
	}
	stopPing();
	const ws = websocket.value;
	websocket.value = undefined;
	ws?.close();
}

if (import.meta.hot) import.meta.hot.dispose(() => stopWebSocket());

export const isConnectedToBridge = ref(false);

function parseWSString(str: string) {
	if (str.startsWith("{") && str.endsWith("}")) {
		return JSON.parse(str);
	}
	return str;
}

function createScaleMessageHandler(ws: WebSocket, scalePath: string, state: ScaleState) {
	const handler = (message) => {
		const wsInput = parseWSString(message.data);

		if (wsInput.address != scalePath) return;

		state.isConnected.value = !wsInput.error;
		state.lastMessageTime.value = performance.now();

		clearInterval(scaleStatusCheck);
		scaleStatusCheck = setInterval(() => {
			state.isConnected.value = false;
		}, 5000);

		if (wsInput.value != null) {
			state.currentWeight.value = Number(wsInput.value.weight);
		}
	};

	ws.addEventListener("message", handler);

	return () => ws?.removeEventListener("message", handler);
}

function createScannerMessageHandler(ws: WebSocket, scannerPath: string, state: ScannerState) {
	const handler = (message) => {
		const wsInput = parseWSString(message.data);
		if (wsInput.address != scannerPath) return;
		state.isConnected.value = !wsInput.error;
		state.lastScannerMessage.value = performance.now();

		clearInterval(scannerStatusCheck);
		scannerStatusCheck = setInterval(() => {
			state.isConnected.value = false;
		}, 5000);

		if (wsInput.value != null) {
			state.valueToUpdate.value = wsInput.value;
		}
	};
	ws.addEventListener("message", handler);

	return () => ws?.removeEventListener("message", handler);
}

watch(websocket, () => {
	isConnectedToBridge.value = websocket.value?.readyState == WebSocket.OPEN;
});

function attemptConnection(): void {
	const existing = websocket.value;
	// Don't pile a new socket on top of a live or in-progress one.
	if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
		return;
	}

	const ws = new WebSocket(WS_BASE_URL);
	// Becomes the current socket immediately, so any old socket's late events see themselves as stale.
	websocket.value = ws;
	if (existing) {
		try {
			existing.close();
		} catch {
			// ignore
		}
	}

	ws.addEventListener("open", () => {
		if (ws !== websocket.value) return; // stale socket
		isConnectedToBridge.value = true;
		fetchUpdates().catch((error) => console.warn("Failed to fetch bridge updates:", error));
		startPing();
	});

	ws.addEventListener("close", () => {
		if (ws !== websocket.value) return; // stale socket closing — no log, no churn
		isConnectedToBridge.value = false;
		stopPing();
		console.log("Connection closed, retrying...");
	});

	ws.addEventListener("error", () => {
		ws.close(); // close THIS socket, not whatever is currently assigned
	});
}

function startPing(): void {
	if (pingInterval != null) return;
	// @ts-ignore
	pingInterval = setInterval(() => {
		const ws = websocket.value;
		if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ destination: "ping" }));
	}, 10000);
}

function stopPing(): void {
	if (pingInterval != null) {
		clearInterval(pingInterval);
		pingInterval = null;
	}
}

export async function setActiveScanner(id: string, lotNumber: Ref<string>, isConnected: Ref<boolean>) {
	await getSerialPortScanner(id).then((response) => {
		const scanner = response;
		const connectionData = JSON.stringify({
			destination: "/serialScanner",
			portData: {
				baudRate: scanner.baudRate,
				numDataBits: scanner.dataBits,
				numStopBits: scanner.stopBits,
				parity: scanner.parity,
				port: scanner.path,
			},
		});
		const setActiveScannerInterval = setInterval(() => {
			const ws = getWebSocket();

			const diff = performance.now() - lastScannerMessage.value;

			if (diff > 2000 && ws.value?.readyState == WebSocket.OPEN) {
				ws.value?.send(connectionData);
				if (scannerHandler) scannerHandler();
				const state = {
					isConnected,
					lastScannerMessage,
					valueToUpdate: lotNumber,
				};
				scannerHandler = createScannerMessageHandler(ws.value, scanner.path, state);
			}

			if (tries >= 3 && !hasBridgeIntegration.value) {
				clearInterval(setActiveScannerInterval);
			}
		}, 1000);
	});
}

export async function setActiveSerialPortScale(
	defaultScale: Ref<SerialPortScale>,
	scale: Ref<SerialPortScale>,
	isConnected: Ref<boolean>,
	currentWeight: Ref<number>,
) {
	await getSerialPortScale(defaultScale.value!.id).then((response) => {
		scale.value = response;
		scale.value.type = "COM";
		scale.value.automatic = defaultScale.value?.automatic ?? false;
		const connectionData = JSON.stringify({
			destination: "/serialScale",
			protocol: scale.value.protocol,
			portData: {
				baudRate: scale.value.baudRate,
				numDataBits: scale.value.dataBits,
				numStopBits: scale.value.stopBits,
				parity: scale.value.parity,
				port: scale.value.path,
			},
		});
		const setActiveScaleInterval = setInterval(() => {
			const ws = getWebSocket();

			const diff = performance.now() - lastMessageTime.value;

			if (diff > 2000 && ws.value?.readyState == WebSocket.OPEN) {
				ws.value?.send(connectionData);
				if (scaleHandler) scaleHandler();

				const state = {
					isConnected,
					lastMessageTime,
					currentWeight,
				};

				scaleHandler = createScaleMessageHandler(ws.value, scale.value.path, state);
			}

			if (tries >= 3 && !hasBridgeIntegration.value) {
				clearInterval(setActiveScaleInterval);
			}
		}, 1000);
	});
}

function bridgeRequest<T = any>(
	destination: string,
	payload: Record<string, unknown> = {},
	timeoutMs = 10000,
): Promise<T> {
	const ws = getWebSocket().value;
	if (ws == null) return Promise.reject(new Error("WebSocket is not connected"));

	const requestId = crypto.randomUUID();

	return new Promise<T>((resolve, reject) => {
		const cleanup = () => {
			ws.removeEventListener("message", handler);
			clearTimeout(timer);
		};

		const timer = setTimeout(() => {
			cleanup();
			reject(new Error(`Bridge request "${destination}" timed out`));
		}, timeoutMs);

		const handler = (message: MessageEvent) => {
			const wsInput = parseWSString(message.data);
			if ((wsInput?.requestId ?? wsInput?.value?.requestId) !== requestId) return;

			cleanup();
			resolve(wsInput.value);
		};

		ws.addEventListener("message", handler);
		ws.send(JSON.stringify({ destination, requestId, ...payload }));
	});
}

export function selectFileSystemEntry(path: string | null, type: string): Promise<string | null> {
	return bridgeRequest<string | null>("/selectFileSystemEntry", { path, type });
}

export async function openFileSystemEntry(path: string | null) {
	const ws = getWebSocket();

	if (ws.value == null) throw new Error("WebSocket is not connected");
	const requestId = crypto.randomUUID();

	const request = JSON.stringify({
		destination: "/openFileSystemEntry",
		path: path,
		requestId,
	});

	ws.value?.send(request);
	return;
}

export async function updateBridgeIntegration(downloadUrl: string): Promise<string> {
	const previousVersion = currentVersion.value;

	let ack: string | undefined;
	try {
		// The bridge downloads the installer before acking, so allow plenty of time.
		ack = await bridgeRequest<string>("/updateBridge", { downloadUrl }, 120_000);
	} catch (e) {
		// The socket typically drops as the bridge restarts right after acking — not fatal.
		console.warn("updateBridge ack not received (bridge may be restarting):", e);
	}

	// A download failure makes the bridge reply with an error string and stay running (no restart).
	if (ack != null && ack !== "Succeeded") throw new Error(ack);

	// The bridge is restarting: wait for it to reconnect and report the new version. The `open`
	// handler re-runs fetchUpdates() on reconnect, which updates currentVersion.
	return waitForBridgeVersion((v) => v != null && v !== previousVersion, 180_000);
}

function waitForBridgeVersion(predicate: (v: string | null) => boolean, timeoutMs: number): Promise<string> {
	return new Promise((resolve, reject) => {
		if (predicate(currentVersion.value)) {
			resolve(currentVersion.value as string);
			return;
		}
		const timer = setTimeout(() => {
			stop();
			reject(new Error("Bridge did not report the updated version in time"));
		}, timeoutMs);
		const stop = watch(currentVersion, (v) => {
			if (predicate(v)) {
				clearTimeout(timer);
				stop();
				resolve(v as string);
			}
		});
	});
}

export async function fetchUpdates(): Promise<void> {
	const res = await bridgeRequest<{ downloadUrl: string; currentVersion: string; latestVersion: string }>(
		"/fetchUpdates",
	);

	downloadUrl.value = res.downloadUrl;
	currentVersion.value = res.currentVersion;
	latestVersion.value = res.latestVersion;
}
